import { useState, type CSSProperties, type ReactNode } from "react";

/** Portato da `components/core/IconButton.jsx` del design system. */

const TONES = {
  light: {
    background: "var(--white)",
    color: "var(--ink-900)",
    border: "1px solid var(--border-strong)",
  },
  dark: { background: "var(--ink-900)", color: "var(--white)", boxShadow: "none" },
  lime: { background: "var(--lime-400)", color: "var(--text-on-lime)", boxShadow: "none" },
  muted: { background: "var(--surface-sunken)", color: "var(--ink-700)", boxShadow: "none" },
  plain: { background: "transparent", color: "var(--ink-400)", boxShadow: "none" },
} satisfies Record<string, CSSProperties>;

export type IconButtonTone = keyof typeof TONES;

export interface IconButtonProps {
  tone?: IconButtonTone;
  size?: number;
  shape?: "circle" | "square";
  /** Obbligatoria: un pulsante di sola icona senza etichetta è muto per uno screen reader. */
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
}

export function IconButton({
  tone = "light",
  size = 40,
  shape = "circle",
  label,
  disabled = false,
  onClick,
  children,
  style,
}: IconButtonProps) {
  const t = TONES[tone];
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        width: size,
        height: size,
        minWidth: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: shape === "square" ? "var(--radius-md)" : "var(--radius-pill)",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
        transform: pressed && !disabled ? "translateY(var(--press-shift))" : "translateY(0)",
        transition: "transform var(--duration-instant) var(--ease-standard)",
        ...t,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
